import type {
  DisputeResponse,
  DisputeListParams,
  DisputeEvidenceRequest,
  DeleteEvidenceRequest,
  DisputeEvidenceBlock,
  DisputeRefundDeclaration,
  DisputeRefundAssociationRequest,
  DisputeRefundAssociationsResponse,
  DisputeWorkspaceRequest,
  DisputeWorkspaceResponse,
  DisputeWorkspaceFilters,
  DisputeWorkspaceAggregate,
  DisputeWorkspaceExport,
  DynamicWorkspaceExportOptions,
  JsonExportOptions,
  PdfExportOptions,
} from '../types';
import type { RequestFn, RequestOptions } from '../client';
import { WORKSPACE_ENVELOPE, downloadExport, negotiated } from '../exportDownload';

/** Workspace filters use CSV values, unlike the transport's repeated-key arrays. */
function workspaceQuery(params?: DisputeWorkspaceRequest): RequestOptions['query'] {
  if (!params) return undefined;
  const { connector, currency, dispute_status, dispute_stage, ...query } = params;
  return {
    ...query,
    ...(connector?.length ? { connector: connector.join(',') } : {}),
    ...(currency?.length ? { currency: currency.join(',') } : {}),
    ...(dispute_status?.length ? { dispute_status: dispute_status.join(',') } : {}),
    ...(dispute_stage?.length ? { dispute_stage: dispute_stage.join(',') } : {}),
  };
}

/** View and respond to payment disputes and chargebacks. */
export class Disputes {
  constructor(private readonly request: RequestFn) {}

  /** Read native disputes and provider-only cases once, after shared authorization and filters. */
  async workspace(params?: DisputeWorkspaceRequest): Promise<DisputeWorkspaceResponse> {
    return this.request('GET', '/disputes/workspace', { query: workspaceQuery(params) });
  }

  /** Read filter options over the same scoped case set; pagination is ignored. */
  async workspaceFilters(params?: DisputeWorkspaceRequest): Promise<DisputeWorkspaceFilters> {
    return this.request('GET', '/disputes/workspace/filters', { query: workspaceQuery(params) });
  }

  /** Count the filtered case set, including unknown outcomes; pagination is ignored. */
  async workspaceAggregate(params?: DisputeWorkspaceRequest): Promise<DisputeWorkspaceAggregate> {
    return this.request('GET', '/disputes/workspace/aggregate', { query: workspaceQuery(params) });
  }

  /**
   * Export the complete filtered set, or throw on the server's explicit
   * export-limit refusal. `GET /disputes/workspace/export`
   *
   * The body is streamed, and held to the `X-Delopay-Record-Count` the server
   * announces and to its own `total_count`: a set that arrives short rejects
   * with `EXPORT_INCOMPLETE` rather than resolving. `options` reports the
   * download's progress and takes a signal and an idle timeout — see
   * {@link ExportTransferOptions}.
   *
   * `format: 'pdf'` resolves to the same set laid out as a document (a
   * `Blob`), with the words in `options.presentation` — see
   * {@link ExportPresentation}. This route offers no CSV.
   */
  async workspaceExport(
    params?: DisputeWorkspaceRequest,
    options?: JsonExportOptions,
  ): Promise<DisputeWorkspaceExport>;
  async workspaceExport(
    params: DisputeWorkspaceRequest | undefined,
    options: PdfExportOptions,
  ): Promise<Blob>;
  async workspaceExport(
    params: DisputeWorkspaceRequest | undefined,
    options: DynamicWorkspaceExportOptions,
  ): Promise<DisputeWorkspaceExport | Blob>;
  async workspaceExport(
    params?: DisputeWorkspaceRequest,
    options?: JsonExportOptions | PdfExportOptions | DynamicWorkspaceExportOptions,
  ): Promise<DisputeWorkspaceExport | Blob> {
    const format = options?.format ?? 'json';
    return downloadExport(
      this.request,
      {
        method: 'GET',
        path: '/disputes/workspace/export',
        request: negotiated({ query: workspaceQuery(params) }, format, options?.presentation),
        format,
        envelope: WORKSPACE_ENVELOPE,
      },
      options,
    );
  }

  /**
   * Retrieve a dispute by its ID.
   *
   * @param disputeId - The unique dispute ID.
   * @returns The dispute.
   */
  async retrieve(disputeId: string): Promise<DisputeResponse> {
    return this.request('GET', `/disputes/${encodeURIComponent(disputeId)}`);
  }

  /** Read declared links and provider-reported refund references. Empty means unknown. */
  async listRefundAssociations(disputeId: string): Promise<DisputeRefundAssociationsResponse> {
    return this.request('GET', `/disputes/${encodeURIComponent(disputeId)}/refund-associations`);
  }

  /** Declare or clear an allocation against a successful refund of this payment. */
  async setRefundAssociation(
    disputeId: string,
    params: DisputeRefundAssociationRequest,
  ): Promise<DisputeRefundDeclaration> {
    return this.request('PUT', `/disputes/${encodeURIComponent(disputeId)}/refund-associations`, {
      body: params,
    });
  }

  /**
   * List disputes, optionally filtered by status, stage, or date range.
   *
   * @param params - Optional filter and pagination parameters.
   * @returns Array of disputes.
   */
  async list(params?: DisputeListParams): Promise<DisputeResponse[]> {
    return this.request('GET', '/disputes/list', {
      query: params as Record<string, string | number | undefined>,
    });
  }

  /**
   * Accept a dispute, conceding the chargeback to the customer.
   *
   * @param disputeId - The dispute ID to accept.
   * @returns The updated dispute.
   */
  async accept(disputeId: string): Promise<DisputeResponse> {
    return this.request('POST', `/disputes/accept/${encodeURIComponent(disputeId)}`);
  }

  /**
   * Submit evidence to challenge a dispute.
   *
   * @param params - Evidence details and the dispute ID to contest.
   * @returns The updated dispute.
   */
  async submitEvidence(params: DisputeEvidenceRequest): Promise<DisputeResponse> {
    return this.request('POST', '/disputes/evidence', { body: params });
  }

  /**
   * Attach evidence (e.g. file upload metadata) to a dispute.
   *
   * Uses `PUT /disputes/evidence`.
   */
  async attachEvidence(params: DisputeEvidenceRequest): Promise<DisputeResponse> {
    return this.request('PUT', '/disputes/evidence', { body: params });
  }

  /**
   * Retrieve previously stored evidence for a dispute.
   *
   * Returns an ARRAY of file-evidence blocks (this was previously mistyped
   * as the flat submit-request shape). Only file evidence is reported —
   * text evidence is not retrievable once submitted.
   *
   * @param disputeId - The dispute ID.
   * @returns The stored file-evidence blocks.
   */
  async retrieveEvidence(disputeId: string): Promise<DisputeEvidenceBlock[]> {
    return this.request('GET', `/disputes/evidence/${encodeURIComponent(disputeId)}`);
  }

  /**
   * Delete submitted evidence for a dispute.
   *
   * @param params - Evidence request body identifying what to delete.
   * @returns The updated dispute.
   */
  async deleteEvidence(params: DeleteEvidenceRequest): Promise<DisputeResponse> {
    return this.request('DELETE', '/disputes/evidence', { body: params });
  }

  // --- OLAP extensions (Task 4.4) ---

  /** List disputes (profile-scoped). `GET /disputes/profile/list` */
  async listByProfile(params?: DisputeListParams): Promise<DisputeResponse[]> {
    return this.request('GET', '/disputes/profile/list', {
      query: params as Record<string, string | number | undefined>,
    });
  }

  /** Get dispute filter options. `GET /disputes/filter` */
  async getFilters(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/disputes/filter', { query: params });
  }

  /** Get dispute filters (profile-scoped). `GET /disputes/profile/filter` */
  async getFiltersByProfile(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/disputes/profile/filter', { query: params });
  }

  /** Get dispute aggregates. `GET /disputes/aggregate` */
  async aggregate(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/disputes/aggregate', { query: params });
  }

  /** Get dispute aggregates (profile-scoped). `GET /disputes/profile/aggregate` */
  async aggregateByProfile(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/disputes/profile/aggregate', { query: params });
  }

  /**
   * Fetch the latest dispute state from the connector (gateway) and persist it.
   * `GET /disputes/{disputeId}?force_sync=true`
   *
   * The path parameter is the **Delopay dispute id** (`dp_…`). Force-sync asks the
   * backend to pull the dispute from the connector (supported where the connector
   * implements the dispute-sync flow, e.g. Stripe) and update the stored record
   * before returning it.
   *
   * Note: this method previously called `GET /disputes/{id}/fetch`, which is a
   * different backend route — a bulk import keyed by **merchant connector account
   * id** with a required date range — so every call with a dispute id failed.
   */
  async fetchFromConnector(disputeId: string): Promise<DisputeResponse> {
    return this.request('GET', `/disputes/${encodeURIComponent(disputeId)}`, {
      query: { force_sync: 'true' },
    });
  }
}
